import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { getCurrentSubscriptionApi } from '../../api/subscription';

// wire shape — the backend's `conventions` middleware snake_cases response `data` payloads
export type SubscriptionApi = {
  id: string;
  user_id: string;
  plan_id: string;
  plan_price_id: string | null;
  pending_plan_id: string | null;
  pending_plan_price_id: string | null;
  status: 'active' | 'cancelled' | 'expired' | 'exhausted' | 'declined';
  current_period_start: string | null;
  current_period_end: string | null;
  trial_ends_at: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
};

export type SubscriptionPlanPriceApi = {
  id: string;
  plan_id: string;
  display_name: string;
  visibility: 'public' | 'internal';
  billing_interval: 'day' | 'month' | 'year' | 'quarter' | 'none';
  interval_count: number;
  amount: number | string;
  currency: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

export type SubscriptionPlanApi = {
  id: string;
  role_id: number | null;
  name: string;
  description: string;
  type: 'public' | 'managed';
  is_active: boolean;
  created_at: string;
  updated_at: string;
  prices: SubscriptionPlanPriceApi[];
};

export type CurrentSubscription = {
  subscription: SubscriptionApi | null;
  plan: SubscriptionPlanApi | null;
};

interface SubscriptionStore {
  // State
  currentSubscription: CurrentSubscription | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchCurrentSubscription: (options?: { silent?: boolean }) => Promise<void>;
  clearSubscription: () => void;
}

// Module-level (not store state) so it dedupes silent and non-silent calls alike —
// isLoading only reflects the non-silent branch and can't be used as the in-flight guard.
let inFlightFetch: Promise<void> | null = null;

const useSubscriptionStore = create<SubscriptionStore>()(
  devtools(
    persist(
      (set) => ({
        // Initial state
        currentSubscription: null,
        isLoading: false,
        error: null,

        fetchCurrentSubscription: async (options = {}) => {
          if (inFlightFetch) {
            console.log('⏳ Subscription fetch already in progress, reusing in-flight request...');
            return inFlightFetch;
          }

          const { silent = false } = options;

          if (silent) {
            set({ error: null });
          } else {
            set({ isLoading: true, error: null });
          }

          inFlightFetch = (async () => {
            try {
              // serviceTokenNew's response interceptor already unwraps the body and rejects on
              // `success !== true`, so a resolved call here is always a success — `res.data` holds
              // { subscription, plan }.
              const res = await getCurrentSubscriptionApi();

              set({
                currentSubscription: res.data ?? null,
                isLoading: false
              });

              console.log('✅ Current subscription loaded successfully:', res.data);
            } catch (error: any) {
              console.error('❌ Failed to fetch current subscription:', error);
              set({
                error: (typeof error === 'string' ? error : error?.message) || 'Failed to fetch subscription',
                isLoading: false
              });
            } finally {
              inFlightFetch = null;
            }
          })();

          return inFlightFetch;
        },

        clearSubscription: () => {
          set({ currentSubscription: null, error: null });
          try {
            localStorage.removeItem('subscription-storage');
          } catch (e) {
            console.error('Failed to clear subscription storage:', e);
          }
        }
      }),
      {
        name: 'subscription-storage',
        partialize: (state) => ({
          currentSubscription: state.currentSubscription
        })
      }
    ),
    { name: 'SubscriptionStore' }
  )
);

export default useSubscriptionStore;
